import { Component, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DataService } from 'src/app/services/data.service';
import { ImageCacheService } from 'src/app/services/image-cache.service';
import { NO_PHOTO } from 'src/app/utils/no-photo';
import { Category } from 'src/interfaces/category.model';
import { Drink } from 'src/interfaces/drink.model';
import { Modificators } from 'src/interfaces/modificators.model';
import { MenuItem } from '../desktop-menu.model';

/**
 * Диалог позиции на десктопе (docs/design/README.md приложения Edim, раздел 16,
 * «Диалог блюда»): только просмотр — варианты меняют цену и вес, добавки без
 * выбора, вместо «Добавить» — «Заказ — в заведении». Esc, клик мимо окна — закрыть
 */
@Component({
    selector: 'app-desktop-item-dialog',
    templateUrl: './desktop-item-dialog.component.html',
    styleUrls: ['./desktop-item-dialog.component.css'],
    standalone: false
})
export class DesktopItemDialogComponent implements OnChanges {
  @Input({ required: true }) entry!: MenuItem;
  /** Все позиции меню — для рекомендаций */
  @Input() items: MenuItem[] = [];
  @Input() categories: Category[] = [];
  @Input() linkCopied = false;

  @Output() close = new EventEmitter<void>();
  @Output() select = new EventEmitter<MenuItem>();
  @Output() copyLink = new EventEmitter<void>();

  /** Выбранный вариант (размер, порция) */
  choice = 0;
  imageSrc = '';
  recommendations: MenuItem[] = [];

  constructor(private dataService: DataService, private imageCache: ImageCacheService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['entry'] && !changes['items']) return;
    const item = this.entry.item;
    if (changes['entry']) {
      this.choice = item.variability && item.variants.length > item.choise ? item.choise : 0;
      this.imageSrc = NO_PHOTO[this.entry.kind];
      if (item.image?.trim()) {
        const id = item.id;
        this.imageCache.getImage(item.image, NO_PHOTO[this.entry.kind]).then((src) => {
          if (this.entry.item.id === id) this.imageSrc = src;
        });
      }
    }
    this.recommendations = this.items.filter((i) => !i.stoplist && item.recomendations?.includes(i.item.id));
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close.emit();
  }

  get category(): Category | undefined {
    return this.categories.find((c) => c.id === this.entry.item.category);
  }

  get variants() {
    return this.entry.item.variability ? this.entry.item.variants : [];
  }

  get price(): number {
    return this.variants[this.choice]?.price ?? this.entry.item.price;
  }

  get size(): number | null {
    return this.variants[this.choice]?.size ?? this.entry.item.size;
  }

  get currency(): string {
    return this.dataService.locale?.currency ?? '';
  }

  get unit(): string {
    return this.dataService.locale?.units?.get(this.entry.item.unit) ?? '';
  }

  get time(): string {
    return this.dataService.locale?.time ?? '';
  }

  get strength(): number | null {
    return this.entry.kind === 'drink' ? (this.entry.item as Drink).strength ?? null : null;
  }

  get modificators(): Modificators | null {
    const value = this.entry.item.modificators;
    return typeof value === 'object' && value?.items?.length ? value : null;
  }

  /** КБЖУ — если указано хоть что-то */
  get nutrition(): { value: string; key: string }[] {
    const item = this.entry.item;
    return [
      { value: item.kkal, key: 'kkal' },
      { value: item.proteins, key: 'protein' },
      { value: item.fats, key: 'fats' },
      { value: item.carbs, key: 'carbs' },
    ]
      .filter((n) => n.value != null && n.value !== 0)
      .map((n) => ({ key: n.key, value: n.value!.toLocaleString(document.documentElement.lang, { maximumFractionDigits: 1 }) }));
  }
}
